import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { RootState } from 'store';

import { DateRange as DateRangeIcon } from '@mui/icons-material';
import { Box, Stack, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';

import CustomButton from 'components/Button';
import SnackBar from 'components/SnackBar';
import { PROJECT_MODAL, projectModalActions } from 'features/Project/projectModalSlice';
import ProjectModal from 'pages/ProjectManagement/component/ProjectModal';
import { useGetManagerListQuery, useGetProjectClientQuery } from 'services/busmanApi';
import { ProjectDetailProps } from 'types/ProjectTypes';
import { PROJECT_STATUS } from 'utils/constants';
import { getRAGStatus, mapProjectResponseToRequest } from 'utils/project';
import { toLocaleString } from 'utils/string';

import DisabledTextField from './component/DisabledText';
import ProjectScopeSection from './sections/ProjectScopeSection';
import { HeaderUnderline, OverviewProjectBox } from './styles';
interface PageProps {
  projectDetails: ProjectDetailProps;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const DATE_FORMAT = ' DD/MM/YYYY';

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

/**
 *
 * @returns Project management overview page
 */
const ProjectManagementOverviewPage = ({ projectDetails }: PageProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { open, mode, modalProps } = useSelector((state: RootState) => state.projectModalReducer);
  const [message, setMessage] = React.useState({ type: '', msg: '' });
  const { data: jobManagers } = useGetManagerListQuery({});
  const { data: projectClients } = useGetProjectClientQuery({});
  const [tabIndex, setTabIndex] = React.useState(0);

  const tabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const formOptions = {
    managers: (jobManagers || []).filter((item) => item.is_active === true),
    clients: projectClients || [],
  };

  const {
    manager,
    status,
    name,
    finish_date: endDate,
    start_date: startDate,
    current_estimated_hours: durationUtilized,
    client_contact: clientDetails,
    estimated_cost: estimatedCost,
    current_estimated_cost: currentEstimatedCost,
    current_scope: currentScope,
  } = projectDetails;

  const projectStatus = Object.values(PROJECT_STATUS).find((item) => item.value === status);
  const projectDuration = dayjs(endDate).diff(dayjs(startDate), 'd') || 0;

  const ragIndicator = () => {
    const greyColor = theme.palette.busmanColors.lightGrey;
    const { rag, reason } = getRAGStatus(projectDetails);
    const circles = [
      { active: rag === 'GREEN', color: theme.palette.busmanColors.busmanGreen },
      { active: rag === 'AMBER', color: theme.palette.busmanColors.busmanWarn },
      { active: rag === 'RED', color: theme.palette.busmanColors.busmanRed },
    ];

    return (
      <Tooltip title={reason}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          {circles.map((c, i) => (
            <Box
              key={i}
              sx={{
                borderRadius: 100,
                background: c.active ? c.color : greyColor,
                width: 10,
                height: 10,
              }}
            />
          ))}
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <OverviewProjectBox sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', paddingBottom: 2 }}>
          <Typography variant="h4">
            Project : {name} - Scope Ver: {currentScope?.version ?? 'Project scope has not been set'}
          </Typography>
          {ragIndicator()}
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabIndex} onChange={tabHandler} aria-label="basic tabs example">
            <Tab label="Overview" {...a11yProps(0)} />
            <Tab label="Scopes" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Overview Panel */}
        <CustomTabPanel value={tabIndex} index={0}>
          <>
            {/* Client */}
            <Box sx={{ mt: 2 }}>
              <DisabledTextField title="CLIENT" displayValue={clientDetails.client.client_name || ''} />
              <Stack direction="row" sx={{ mt: 1 }} gap={2}>
                <Stack direction="column" width="100%">
                  <DisabledTextField title="MANAGER" displayValue={manager[0].full_name || ''} />
                </Stack>
                <Stack direction="column" width="100%">
                  <DisabledTextField title="STATUS" displayValue={projectStatus?.name || ''} />
                </Stack>
              </Stack>
            </Box>

            {/* Primary Contact */}
            <Box sx={{ mt: 1 }}>
              <HeaderUnderline>
                <Typography variant="h4">Primary Contact</Typography>
              </HeaderUnderline>
              <Stack direction="row" sx={{ mt: 1 }} gap={2}>
                <Stack direction="column" width="100%">
                  <DisabledTextField title="POSITION OR ROLE" displayValue={clientDetails.position || ''} />
                </Stack>
                <Stack direction="column" width="100%">
                  <DisabledTextField title="FULL NAME" displayValue={clientDetails?.full_name || ''} />
                </Stack>
              </Stack>

              <Stack direction="row" sx={{ mt: 1, minWidth: 0 }} gap={2}>
                <Stack direction="column" width="100%">
                  <DisabledTextField title="EMAIL" displayValue={clientDetails.email || ''} />
                </Stack>
                <Stack direction="column" width="100%">
                  <DisabledTextField title="PHONE (OPTIONAL)" displayValue={clientDetails.phone || '00 0000 0000'} />
                </Stack>
              </Stack>
            </Box>

            {/* Time */}
            <Box sx={{ mt: 1 }}>
              <HeaderUnderline>
                <Typography variant="h4">Time</Typography>
              </HeaderUnderline>
              <Stack direction="row" gap={2} sx={{ mt: 1 }}>
                <Stack direction="column" width="100%">
                  <DisabledTextField
                    title="START (PROJECTED)"
                    displayValue={startDate ? dayjs(startDate).format(DATE_FORMAT) : DATE_FORMAT}
                    startIcon={<DateRangeIcon />}
                  />
                </Stack>
                <Stack direction="column" width="100%">
                  <DisabledTextField
                    title="FINISH (PROJECTED)"
                    displayValue={endDate ? dayjs(endDate).format(DATE_FORMAT) : DATE_FORMAT}
                    startIcon={<DateRangeIcon />}
                  />
                </Stack>
              </Stack>

              <Stack direction="row" gap={2} sx={{ mt: 1 }}>
                <Stack direction="column" width="100%">
                  <DisabledTextField
                    title="DURATION (DAYS)"
                    displayValue={`${projectDuration}`}
                    startIcon={<DateRangeIcon />}
                  />
                </Stack>
                <Stack direction="column" width="100%">
                  <DisabledTextField
                    title="DURATION UTILISED (DAYS)"
                    displayValue={
                      typeof durationUtilized === 'number' && durationUtilized > 0
                        ? dayjs.duration(durationUtilized, 'hours').humanize()
                        : '0'
                    }
                    startIcon={<DateRangeIcon />}
                  />
                </Stack>
              </Stack>
            </Box>

            {/* COST */}
            <Box sx={{ mt: 1 }}>
              <HeaderUnderline>
                <Typography variant="h4">Cost</Typography>
              </HeaderUnderline>

              <Stack direction="row" gap={2} sx={{ mt: 1 }}>
                <Stack direction="column" width="100%">
                  <DisabledTextField
                    title="COST (PROJECTED)"
                    displayValue={toLocaleString(estimatedCost, 'currency', 0)}
                  />
                </Stack>
                <Stack direction="column" width="100%">
                  <DisabledTextField
                    title="COST (LIVE)"
                    displayValue={toLocaleString(currentEstimatedCost, 'currency', 0)}
                  />
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ mt: 10 }}>
              <CustomButton
                onClick={() =>
                  dispatch(
                    projectModalActions.openEditModal({
                      type: PROJECT_MODAL,
                      data: mapProjectResponseToRequest(projectDetails),
                    }),
                  )
                }
                text="Edit Project"
                variant="contained_grey"
              />
            </Box>
          </>

          {open && modalProps?.type === PROJECT_MODAL && (
            <ProjectModal
              open={open}
              isEditing={mode === 'edit'}
              formOptions={formOptions}
              setSnackBarMessage={setMessage}
              onClose={() => dispatch(projectModalActions.closeModal())}
              initialValues={modalProps?.type === PROJECT_MODAL ? modalProps.data : null}
            />
          )}
        </CustomTabPanel>

        {/* Scope Panel */}
        <CustomTabPanel value={tabIndex} index={1}>
          <ProjectScopeSection projectDetails={projectDetails} />
        </CustomTabPanel>
        <SnackBar
          message={message}
          onClose={() => setMessage({ type: '', msg: '' })}
          snackbarOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </OverviewProjectBox>
    </>
  );
};

export default ProjectManagementOverviewPage;
